import type { IntlShape } from "@formatjs/intl";
import type { Dispatch } from "@reduxjs/toolkit";
import { Button, Form, Skeleton } from "antd";
import type { HookAPI } from "antd/es/modal/useModal";
import dayjs from "dayjs";
import { useEffect, useMemo, useRef } from "react";
import { connect } from "react-redux";
import { actions as routerActions } from "redux-router5";
import ConnectedAppLayout from "../AppLayout";
import type { RootState } from "../store";
import ModalFooter from "../../common/components/ModalFooter";
import { routeNames } from "../common/routes";
import { ticketDataActions } from "../../features/ticket-data/ticketDataSlice";
import { ticketActions } from "../../features/ticket/ticketSlice";
import type {
	TurbineComponent,
	Client,
	Location,
	Materials,
	Project,
	Services,
	Status,
	TicketRetrieve,
	Turbine,
	UserLimitedList,
} from "../generated-axios-client";
import { UpdateTicketFields } from "./UpdateTicketFields";
import { useServiceMaterialsSync } from "./useServiceMaterialsSync";

type UpdateTicketFormProps = {
	dispatch: Dispatch;
	initialValue?: TicketRetrieve | null;
	intl: IntlShape;
	serviceItems: Services[] | null;
	userItems?: UserLimitedList[];
	coordinatorItems?: UserLimitedList[];
	managerItems?: UserLimitedList[];
	statusItems: Status[] | null;
	turbineItems?: Turbine[];
	clientItems?: Client[];
	componentItems?: TurbineComponent[];
	locationItems?: Location[] | null;
	materialItems?: Materials[] | null;
	projectItems?: Project[];
	modalAPI?: HookAPI;
};

const fields = new Set(["turbine", "client"]);

export function UpdateTicketForm(props: UpdateTicketFormProps) {
	const {
		dispatch,
		initialValue,
		intl,
		serviceItems,
		userItems,
		coordinatorItems,
		managerItems,
		statusItems,
		turbineItems,
		clientItems,
		componentItems,
		locationItems,
		materialItems,
		projectItems,
		modalAPI,
	} = props;

	const [form] = Form.useForm();
	const prevServiceRef = useRef<string | null>(null);
	const { snapshotServiceIds, syncServiceMaterials } = useServiceMaterialsSync(
		form,
		serviceItems,
		initialValue?.materials ?? [],
	);

	const saveTicket = () => {
		form
			.validateFields()
			.then((values) => {
				const cleanedItems = (
					(values.items ?? []) as Array<Record<string, unknown>>
				).map((item) => ({
					...item,
					service: (item.service as string | undefined) ?? null,
					description: (item.description as string | undefined) ?? null,
					estimated_time_spent:
						item.estimated_time_spent != null
							? String(item.estimated_time_spent as number)
							: null,
					completed: (item.completed as boolean | undefined) ?? false,
				}));

				const editedValues = {
					...values,
					items: cleanedItems,
					coordinators:
						(values.coordinators as number[] | undefined)?.map((id) => ({
							coordinator: id,
						})) ?? [],
					managers:
						(values.managers as number[] | undefined)?.map((id) => ({
							manager: id,
						})) ?? [],
					assignees:
						(values.assignees as number[] | undefined)?.map((id) => ({
							assignee: id,
						})) ?? [],
					materials: (values.materials as string[] | undefined)?.map((id) => ({
						material: id,
					})),
					start: values.start ? values.start.format("YYYY-MM-DD") : null,
					deadline: values.deadline ? values.deadline.format("YYYY-MM-DD") : null,
					client_case_nr: values.client_case_nr,
				};

				if (initialValue?.id) {
					editedValues.id = initialValue.id;
				}

				dispatch(ticketActions.addOrUpdateTicket(editedValues));
			})
			.catch((errorInfo) => {
				console.error("Validation failed:", errorInfo);
			});
	};

	const initialValues = useMemo(() => {
		return initialValue
			? {
					...initialValue,
					service: initialValue.service?.id ?? null,
					client: initialValue.client.id,
					turbine: initialValue.turbine.id,
					start: initialValue.start ? dayjs(initialValue.start) : null,
					deadline: initialValue.deadline ? dayjs(initialValue.deadline) : null,
					client_case_nr: initialValue.client_case_nr,
					location: initialValue.location.id,
					items: initialValue.items.map((item) => {
						const parsedEstimate = Number(item.estimated_time_spent);
						return {
							...item,
							component: item.component?.id ?? item.component,
							estimated_time_spent: Number.isNaN(parsedEstimate)
								? undefined
								: parsedEstimate,
						};
					}),
					coordinators: initialValue.coordinators.map((c) => c.coordinator.id),
					managers: initialValue.managers.map((m) => m.manager.id),
					assignees: initialValue.assignees.map((a) => a.assignee.id),
					materials: initialValue.materials.map((m) => m.material),
					project: initialValue.project?.id ?? null,
				}
			: undefined;
	}, [initialValue]);

	// sync initial values
	useEffect(() => {
		if (initialValues) {
			form.setFieldsValue(initialValues);
			prevServiceRef.current = (initialValues.service as string | null) ?? null;
			snapshotServiceIds();
			dispatch(
				ticketDataActions.fetchTicketData({
					client: initialValues.client,
					turbine: initialValues.turbine,
				}),
			);
		} else {
			form.resetFields();
			snapshotServiceIds();
		}
	}, [initialValues, form, dispatch, snapshotServiceIds]);

	const relevantFieldsChanged = (
		changedValues: Record<string, unknown>,
		allValues: Record<string, unknown>,
	) => {
		if ("service" in changedValues) {
			const newServiceId = changedValues.service as string | null | undefined;
			const prevService = prevServiceRef.current;
			if (newServiceId && prevService) {
				modalAPI?.confirm({
					title: intl.formatMessage({ id: "service_change_confirm_title" }),
					content: intl.formatMessage({
						id: "service_change_confirm_content",
					}),
					okText: intl.formatMessage({ id: "confirm" }),
					cancelText: intl.formatMessage({ id: "cancel" }),
					onOk() {
						syncServiceMaterials();
						prevServiceRef.current = newServiceId;
					},
					onCancel() {
						form.setFieldValue("service", prevService);
					},
				});
			} else {
				syncServiceMaterials();
				prevServiceRef.current = newServiceId ?? null;
			}
		} else if ("items" in changedValues) {
			syncServiceMaterials();
		}

		// The site is derived from the turbine, so it must not be sent as a filter:
		// it would narrow the turbine list to the previously derived site and the
		// turbine selector would drop the turbine that was just picked.
		for (const [key, value] of Object.entries(changedValues)) {
			if (
				(fields.has(key) && value) ||
				(fields.has(key) && key !== "turbine")
			) {
				dispatch(
					ticketDataActions.fetchTicketData({
						client: allValues.client as string | undefined,
						turbine: allValues.turbine as string | undefined,
					}),
				);
			}
		}
	};

	if (
		!serviceItems ||
		!userItems ||
		!statusItems ||
		!turbineItems ||
		!clientItems ||
		!locationItems
	) {
		return <Skeleton active />;
	}

	return (
		<ConnectedAppLayout>
			<Form
				form={form}
				layout="vertical"
				initialValues={initialValues}
				onValuesChange={(changedValues, allValues) =>
					relevantFieldsChanged(changedValues, allValues)
				}
			>
				<UpdateTicketFields
					intl={intl}
					serviceItems={serviceItems}
					userItems={userItems}
					coordinatorItems={coordinatorItems ?? []}
					managerItems={managerItems ?? []}
					statusItems={statusItems}
					turbineItems={turbineItems}
					clientItems={clientItems}
					componentItems={componentItems}
					form={form}
					locationItems={locationItems}
					materialItems={materialItems ?? []}
					projectItems={projectItems ?? []}
				/>
			</Form>
			<ModalFooter>
				<Button
					onClick={() =>
						dispatch(
							routerActions.navigateTo(routeNames.TICKET_VIEW, {
								id: initialValue?.id,
							}),
						)
					}
				>
					{intl.formatMessage({ id: "ticket_button_title_cancel" })}
				</Button>
				<Button type="primary" onClick={saveTicket}>
					{intl.formatMessage({ id: "ticket_button_title_create" })}
				</Button>
			</ModalFooter>
		</ConnectedAppLayout>
	);
}

export const UpdateTicketFormConnected = connect((state: RootState) => {
	const mappedProps = {
		intl: state.intl.datetimeIntl,
		serviceItems: state.ticket.services,
		userItems: state.ticketData.technicians,
		coordinatorItems: state.ticketData.coordinators,
		managerItems: state.ticketData.managers,
		turbineItems: state.ticketData.turbines,
		clientItems: state.ticketData.clients,
		componentItems: state.ticketData.components,
		initialValue: state.ticket.ticket,
		statusItems: state.ticket.statuses,
		locationItems: state.ticketData.locations,
		materialItems: state.ticketData.materials,
		projectItems: state.ticketData.projects ?? [],
		modalAPI: state.appFeedback.modal,
	};
	return mappedProps;
})(UpdateTicketForm);

export default UpdateTicketFormConnected;
